// Site footer: groups of links (a title, which can link too, and a list of links to pages or addresses) and a line of text, with the
// Privacy settings link. Admins with edit rights get an edit button in edit mode (FooterEditor.js). Content: GET /api/footer (apps/portal/site-footer.js).
// One rounded card as wide as the page grid (same Container as the main content): slim and floating above the bottom of the window while you
// browse; at the end of the page it grows and opens the link groups.
(function () {
  const { Box, Typography, Container } = MaterialUI;
  const { useState, useEffect, useLayoutEffect, useRef } = React;

  const DEFAULT = { text: '© 2026 whimsicalbox.uk • A place for us to reflect on our accomplishments by Alex M', groups: [], canEdit: false };
  const CACHE = 'wb_footer';
  const cached = () => { try { return { ...DEFAULT, ...JSON.parse(localStorage.getItem(CACHE) || 'null'), canEdit: false }; } catch (e) { return DEFAULT; } };
  // A page, optionally with one of its sections and an article in it (the same addresses the site uses)
  const pageHref = (page, sec, slug) => '#/' + [...(page === 'about' ? ['portfolio'] : ['p', page]), sec, slug].filter(Boolean).join('/');
  const hrefOf = t => window.SiteLinks.href(t);
  const external = href => /^https?:/.test(href);

  // Background like a grid block: a theme container colour or a custom colour, with the text following it; absent = the translucent default
  function surfaceOf(d) {
    const { GRID_SURFACE, customSurface } = window.Components.Surfaces || {};
    if (!d.surface || !GRID_SURFACE) return null;
    if (d.surface === 'custom') return d.bg ? customSurface(d.bg) : null;
    const pair = GRID_SURFACE[d.surface];
    if (!pair) return null;
    const [bg, ink] = pair;
    return { backgroundColor: bg, color: ink, '--m3-on-surface': ink, '--m3-on-surface-variant': `color-mix(in srgb, ${ink} 80%, transparent)`, '--m3-primary': ink, '--m3-outline': `color-mix(in srgb, ${ink} 50%, transparent)`, '--m3-outline-variant': `color-mix(in srgb, ${ink} 22%, transparent)` };
  }
  const PAD = { small: { x: 2, y: 2, slim: 0.25 }, large: { x: 5, y: 6, slim: 1.5 } };

  // A link (or plain text when it has no address) with its optional icon in front
  function FooterLink({ to, children, sx, ...rest }) {
    const { GoogleIcon } = window.Components;
    const href = hrefOf(to);
    const content = to.icon ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}><GoogleIcon name={to.icon} size={18} style={{ color: 'inherit' }} />{children}</Box> : children;
    // A pill stands out as a tonal chip; its colours come from the text colour, so it also works on a coloured footer
    const pill = to.pill ? { display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.5, borderRadius: 'var(--m3-shape-full)', fontWeight: 500, color: 'var(--m3-on-surface)', backgroundColor: 'color-mix(in srgb, var(--m3-on-surface) 12%, transparent)', '&:hover': { textDecoration: 'none', '--m3-icon-fill': 1, backgroundColor: 'color-mix(in srgb, var(--m3-on-surface) 20%, transparent)' } } : null;
    if (!href) return <Box component="span" sx={{ ...pill, ...sx }}>{content}</Box>;
    return (
      <Box component="a" href={href} {...(external(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})} sx={{ color: 'inherit', textDecoration: 'none', '--m3-icon-fill': 0, '&:hover, &:focus-visible': { '--m3-icon-fill': 1 }, '&:hover': { textDecoration: 'underline' }, '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: 2, borderRadius: pill ? 'var(--m3-shape-full)' : '4px' }, ...pill, ...sx }} {...rest}>{content}</Box>
    );
  }

  function SiteFooter({ user }) {
    const { IconButton, Tooltip, GoogleIcon } = window.Components;
    const editOn = window.EditMode.use();
    const [data, setData] = useState(cached);
    const [dialog, setDialog] = useState(false);
    const load = () => {
      let token = ''; try { token = localStorage.getItem('wb_token') || ''; } catch (e) { /* no storage */ }
      fetch('/api/footer', token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
        .then(r => (r.ok ? r.json() : Promise.reject(new Error('footer'))))
        .then(d => { setData(d); try { localStorage.setItem(CACHE, JSON.stringify({ text: d.text, groups: d.groups, surface: d.surface, bg: d.bg, pad: d.pad })); } catch (e) { /* no storage */ } })
        .catch(() => {});
    };
    useEffect(load, [user && user.id]);
    const groups = (data.groups || []).filter(g => g.title || (g.links || []).length);
    const grouped = groups.length > 0;
    const tint = surfaceOf(data);
    const pad = PAD[data.pad];

    // One card. Slim, it floats above the bottom of the window with the text and Privacy settings. When the end of the page comes into view it
    // grows upward and opens the link groups (the page reserves that room at its end, so its height never changes and nothing jumps). Motion:
    // emphasized-decelerate easing, long duration; the groups follow one after another; none with reduced motion. On phones it is simply open.
    const [opened, setOpened] = useState(false);
    const [poked, setPoked] = useState(false); // pointer over the footer or focus inside it: opens it on pages too short to scroll
    const open = opened || poked;
    const [room, setRoom] = useState(0); // height of the opened link groups
    const endRef = useRef(null);
    const innerRef = useRef(null);
    // Never opens by itself while a page is loading (its content is short for a moment, so the end of the page is in view): it waits for the
    // visitor to scroll (or use the wheel, touch or keyboard); on a page too short to scroll, hovering or focusing the footer opens it. Each page change starts again.
    const [armed, setArmed] = useState(false);
    const [visit, setVisit] = useState(0);
    useEffect(() => { const on = () => { setArmed(false); setVisit(v => v + 1); }; window.addEventListener('hashchange', on); return () => window.removeEventListener('hashchange', on); }, []);
    useEffect(() => {
      if (armed) return undefined;
      const arm = () => setArmed(true);
      const events = ['wheel', 'touchmove', 'keydown'];
      events.forEach(n => window.addEventListener(n, arm, { passive: true, once: true }));
      // scrolling counts once the load's own scroll restoring / jumps to the top are over
      const t = setTimeout(() => window.addEventListener('scroll', arm, { passive: true, once: true }), 1500);
      return () => { clearTimeout(t); events.forEach(n => window.removeEventListener(n, arm)); window.removeEventListener('scroll', arm); };
    }, [armed, visit]);
    useEffect(() => {
      const el = endRef.current;
      if (!grouped) { setOpened(false); return undefined; } // nothing to open (yet): staying shut keeps the card slim when the groups arrive (no grow + collapse)
      if (!el || !('IntersectionObserver' in window)) { setOpened(true); return undefined; }
      const io = new IntersectionObserver(([e]) => setOpened(armed && e.isIntersecting), { rootMargin: '0px 0px -24px 0px' });
      io.observe(el);
      return () => io.disconnect();
    }, [grouped, armed]);
    // Layout effect: the room is measured before the groups are first painted, so the footer's reserved height never grows on screen (CLS)
    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!grouped || !el) return undefined;
      setRoom(el.offsetHeight);
      if (!('ResizeObserver' in window)) return undefined;
      const ro = new ResizeObserver(() => setRoom(el.offsetHeight));
      ro.observe(el);
      return () => ro.disconnect();
    }, [grouped, groups.length]);
    const ease = 'var(--m3-duration-long) var(--m3-easing-emphasized-decelerate)';
    const still = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } };
    const reveal = i => ({
      opacity: { xs: 1, md: open ? 1 : 0 }, transform: { xs: 'none', md: open ? 'none' : 'translateY(20px)' },
      transition: `opacity ${ease}, transform ${ease}`, transitionDelay: open ? `${120 + i * 70}ms` : '0ms', ...still
    });
    const cardSx = {
      pointerEvents: 'auto', position: 'relative', color: 'var(--m3-on-surface-variant)', overflow: 'hidden',
      backgroundColor: 'color-mix(in srgb, var(--m3-surface-container) 88%, transparent)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
      border: '1px solid var(--m3-outline-variant)', boxShadow: 'var(--m3-elevation-2)',
      transition: `border-radius ${ease}, background-color 0.25s cubic-bezier(0.2, 0, 0, 1), border-color 0.25s cubic-bezier(0.2, 0, 0, 1)`, ...still,
      ...(tint && { backdropFilter: 'none', WebkitBackdropFilter: 'none', ...tint })
    };
    const px = pad ? pad.x : { xs: 2.5, md: grouped ? 4 : 3 };
    const SLIM = 60; // the slim card plus its gap to the window edge

    return (
      <>
        {/* Marks where the page content ends: when it scrolls into view the footer opens */}
        {grouped && <Box ref={endRef} aria-hidden="true" sx={{ height: '1px', display: { xs: 'none', md: 'block' } }} />}
        {/* Desktop: the footer is fixed to the window bottom and this keeps its room at the end of the page. Not position: sticky, because
            Chrome scores a sticky element against its unstuck place, so every bit of content loading above it counted as a layout shift */}
        <Box aria-hidden="true" sx={{ display: { xs: 'none', md: 'block' }, height: grouped ? room + SLIM : SLIM }} />
        <Box component="footer" sx={{
          display: { xs: grouped || !user ? 'block' : 'none', md: 'block' }, position: { xs: 'static', md: 'fixed' }, left: { md: 0 }, right: { md: 0 }, bottom: 0, zIndex: { xs: 0, md: 1100 }, pointerEvents: 'none',
          ...(grouped && { height: { md: 0 } }) // the card is anchored to its bottom edge and grows upward; a box growing with it would count as a layout shift
        }}>
          <Container maxWidth="lg" sx={{
            pb: { xs: grouped ? 'calc(80px + env(safe-area-inset-bottom, 0px) + 16px)' : 'calc(80px + env(safe-area-inset-bottom, 0px) + 8px)', md: 2 },
            pt: { xs: grouped ? 4 : 0, md: 0 }, ...(grouped && { position: { md: 'absolute' }, left: { md: 0 }, right: { md: 0 }, bottom: { md: 0 } })
          }}>
            <Box onPointerEnter={e => { if (e.pointerType === 'mouse') setPoked(true); }} onPointerLeave={() => setPoked(false)} onFocusCapture={() => setPoked(true)} onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget)) setPoked(false); }}
              sx={{ ...cardSx, borderRadius: open && grouped ? 'var(--m3-shape-extra-large)' : { xs: grouped ? 'var(--m3-shape-extra-large)' : 'var(--m3-shape-large)', md: 'var(--m3-shape-large)' }, px, py: 0 }}>
              {grouped && (
                <Box sx={{ display: 'grid', gridTemplateRows: { xs: '1fr', md: open ? '1fr' : '0fr' }, transition: `grid-template-rows ${ease}`, ...still }}>
                  <Box sx={{ minHeight: 0, overflow: 'hidden' }}>
                    <Box ref={innerRef} sx={{ pt: pad ? pad.y : { xs: 3, md: 4 }, pb: 3 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fit, minmax(150px, max-content))' }, columnGap: { xs: 3, md: 8 }, rowGap: 3 }}>
                        {groups.map((g, i) => (
                          <Box key={i} component="nav" aria-label={g.title || 'Links'} sx={reveal(i)}>
                            {g.title && (
                              <Typography variant="subtitle2" component="p" sx={{ m: 0, mb: 1, fontWeight: 600, color: 'var(--m3-on-surface)' }}>
                                <FooterLink to={g}>{g.title}</FooterLink>
                              </Typography>
                            )}
                            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              {(g.links || []).map((l, k) => (
                                <li key={k} style={{ alignSelf: 'flex-start' }}><Typography variant="body2" component="span"><FooterLink to={l}>{l.label}</FooterLink></Typography></li>
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', columnGap: 1, py: pad ? pad.slim : 0.5, ...(grouped && open && { borderTop: '1px solid var(--m3-outline-variant)' }), transition: 'border-color 0.25s' }}>
                <Typography variant="caption" component="p" sx={{ m: 0 }}>
                  <Box component="span" sx={{ display: { xs: grouped ? 'inline' : 'none', md: 'inline' } }}>{data.text}{data.text ? ' • ' : ''}</Box>
                  <Box component="button" type="button" onClick={() => window.Consent.openSettings()} sx={{ font: 'inherit', color: 'inherit', background: 'none', border: 0, py: 1, px: 0.5, mx: -0.5, cursor: 'pointer', textDecoration: 'underline' }}>Privacy settings</Box>
                </Typography>
                {editOn && data.canEdit && (
                  <Tooltip title="Edit footer"><IconButton size="small" aria-label="Edit footer" onClick={() => setDialog(true)}><GoogleIcon name="edit" size={20} /></IconButton></Tooltip>
                )}
              </Box>
            </Box>
          </Container>
        </Box>
        {dialog && <window.Components.FooterEditor data={data} onClose={() => setDialog(false)} onSaved={d => { setData(d); setDialog(false); load(); window.notify('Footer saved'); }} />}
      </>
    );
  }

  window.Components = window.Components || {};
  window.Components.SiteFooter = SiteFooter;
})();
